import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";
import { Camera, CameraOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Camera QR reader (jsQR). Calls `onResult(text)` once per distinct code, then pauses ~2.5 s.
 * Shows a reticle + scan line while running; a clear message when the camera is unavailable.
 */
export const QrScanner = ({ onResult, active = true, className, overlay }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const lastRef = useRef({ text: "", at: 0 });
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!active) return undefined;
    let stream;
    let cancelled = false;
    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setError("Kameraet er ikkje tilgjengeleg i denne nettlesaren. Skriv inn koden under."); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        const v = videoRef.current;
        v.srcObject = stream;
        v.setAttribute("playsinline", "true");
        await v.play();
        setRunning(true);
        setError("");
        const tick = () => {
          const video = videoRef.current;
          const canvas = canvasRef.current;
          if (!video || !canvas) return;
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            const w = Math.min(video.videoWidth, 640);
            const h = Math.round(video.videoHeight * (w / video.videoWidth));
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            ctx.drawImage(video, 0, 0, w, h);
            const img = ctx.getImageData(0, 0, w, h);
            const code = jsQR(img.data, w, h, { inversionAttempts: "dontInvert" });
            if (code?.data) {
              const now = Date.now();
              if (code.data !== lastRef.current.text || now - lastRef.current.at > 2500) {
                lastRef.current = { text: code.data, at: now };
                onResult(code.data);
              }
            }
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        setError(e?.name === "NotAllowedError" ? "Gje nettlesaren lov til å bruke kameraet, eller skriv inn koden under." : "Fekk ikkje starta kameraet. Skriv inn koden under.");
      }
    };
    start();
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setRunning(false);
    };
  }, [active, onResult]);

  return (
    <div className={cn("relative overflow-hidden rounded-[20px] bg-ink", className)} data-testid="qr-scanner" data-running={running}>
      <video ref={videoRef} muted className={cn("aspect-[4/3] w-full object-cover sm:aspect-[16/10]", !running && "opacity-0")} />
      <canvas ref={canvasRef} className="hidden" />
      {running && (
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0 bg-ink/35 [mask-image:radial-gradient(ellipse_at_center,transparent_34%,black_36%)]" />
          <div className="reticle absolute left-1/2 top-1/2 h-[62%] w-[62%] max-w-[300px] -translate-x-1/2 -translate-y-1/2"><i /><span className="scan-line" /></div>
        </div>
      )}
      {overlay && <div className="pointer-events-none absolute inset-x-0 top-0 p-4">{overlay}</div>}
      {!running && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center text-paper">
          {error ? <CameraOff className="h-6 w-6 text-paper/70" /> : <Camera className="h-6 w-6 animate-pulse text-paper/70" />}
          <p className="max-w-xs text-[14px] leading-relaxed text-paper/80" data-testid="qr-scanner-status">{error || "Startar kameraet …"}</p>
          {error && <Button type="button" size="sm" variant="outline" className="border-paper/30 bg-transparent text-paper hover:bg-paper/10" onClick={() => window.location.reload()} data-testid="checkin-scanner-start">Prøv igjen</Button>}
        </div>
      )}
    </div>
  );
};
